import React, { useEffect, useState, Suspense, lazy } from 'react';
import {
	createBrowserRouter,
	createRoutesFromElements,
	Navigate,
	Route,
	RouterProvider,
	useRouteError,
} from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { isEmpty, isNil } from 'lodash';

import Cookies from 'js-cookie';
import jwt_decode from 'jwt-decode';

import { ErrorBoundary } from 'react-error-boundary';

import BayLoader from '../bay-ui/src/BayLoader';
import BayModal from '../bay-ui/src/BayModal';
import BayToast from '../bay-ui/src/BayToast';
import ErrorElement from '../bay-ui/src/ErrorElement';
import { closeToast } from '../state/slices/toastSlice';

import globalState from '../state/global';

import api from '../services';

import { getAccess } from '../services/modules/access';

import LegacyRedirect from './LegacyRedirect';

import { useFeatureIsOn, useGrowthBook } from '@growthbook/growthbook-react';

const App = lazy(() => import('../components/App'));
//TODO: unify naming scheme for all components
const Login = lazy(() => import('../components/pages/login'));
const Logout = lazy(() => import('../components/pages/logout'));
const Index = lazy(() => import('../components/pages/index'));
const ReadyToShare = lazy(() => import('../components/pages/ReadyToShare'));
const BrandAssets = lazy(() => import('../components/pages/BrandAssets'));
const Templates = lazy(() => import('../components/pages/templates/templates'));
const EmailTemplates = lazy(
	() => import('../components/pages/templates/email-templates'),
);
const EmailBlockLibrary = lazy(
	() => import('../components/pages/templates/email-block-library'),
);
const Template = lazy(() => import('../components/pages/template'));
const Headline = lazy(() => import('../components/pages/headline'));
const Headlines = lazy(() => import('../components/pages/headlines'));
// Test surface for the BayFloatManager engine -- see
// bay-ui/.plan/feat-overlay-consolidation. Unguarded on purpose so it is reachable
// without a session. Delete with the page when phase 2 lands.
const FloatManagerLab = lazy(() => import('../components/pages/FloatManagerLab'));
const TooltipLab = lazy(() => import('../components/pages/TooltipLab'));
//const Settings = lazy(() => import("../components/pages/settings"));
// Template Settings Pages
const GeneralSettings = lazy(
	() => import('../components/pages/template-settings/General'),
);
const CategoriesSettings = lazy(
	() => import('../components/pages/template-settings/Categories'),
);
const TagsSettings = lazy(
	() => import('../components/pages/template-settings/Tags'),
);
const KeywordsSettings = lazy(
	() => import('../components/pages/template-settings/Keywords'),
);
const PresetsSettings = lazy(
	() => import('../components/pages/template-settings/Presets'),
);
const NotFoundPage = lazy(() => import('../components/pages/notFound'));

const Router = () => {
	const userState = globalState.useUserState();
	const actingAsState = globalState.useActingAsState();
	const notificationState = globalState.useNotificationState();

	const globalDispatch = globalState.useDispatch();

	let bayToken = Cookies.get('bayToken');

	const gb = useGrowthBook();
	const { t } = useTranslation();
	const access = getAccess();

	const isBlockEditorEnabled = useFeatureIsOn('email_block_editor');

	useEffect(() => {
		if (!isNil(bayToken)) {
			handleSessionManagement();
		}
	}, []);

	useEffect(() => {
		if (access?.isGlobalRole) {
			document.title = `${t('contentManager')} - ${t('globalAdmin')}`;
			return;
		}
	}, [access?.isGlobalRole, t]);

	const logout = () => {
		sessionStorage.clear();
		Cookies.remove('bayToken', { domain: '.backatyou.com' });
		Cookies.remove('dsaDirect', { domain: '.backatyou.com' });
		// We use window here to force a hard refresh
		window.location.href = '/login';
	};

	const handleSessionManagement = async () => {
		await api.login
			.refresh()
			.then((resp) => {
				if (resp.status === 0) {
					if (isNil(userState)) {
						globalDispatch(globalState.setUserState(resp.result.session.user));
						globalDispatch(
							globalState.setActingAsState(resp.result.session.user),
						);
					}
				} else {
					logout();
				}
			})
			.catch((e) => {
				//logout();
				console.error('refresh error', e);
			});
	};

	const logError = (error, info) => {
		// TODO: Do something with the error, e.g. log to an external API
		console.warn('Design Manager Error Boundary:', error, info);
	};

	const FallbackErrorElement = ({ resetErrorBoundary }) => {
		// NOTE: Call resetErrorBoundary() to reset the error boundary and retry the render.
		return (
			<ErrorElement
				imgSrc={'/assets/img/Error.svg'}
				resetErrorBoundary={resetErrorBoundary}
			/>
		);
	};

	const RouteErrorElement = () => {
		// NOTE: You could use the error information here if needed
		//let error = useRouteError();
		return <ErrorElement imgSrc={'/assets/img/Error.svg'} />;
	};

	// TODO: Refactor the routes to be more DRY and programmatic
	const router =
		gb?.ready &&
		createBrowserRouter(
			createRoutesFromElements(
				<Route element={<App />}>
					<Route
						errorElement={<RouteErrorElement />}
						path="/"
						element={
							!isEmpty(bayToken) && !isNil(bayToken) ? (
								<Navigate to="/design-templates" replace />
							) : (
								<Navigate to="/login" replace />
							)
						}
					/>

					<Route
						errorElement={<RouteErrorElement />}
						path="/ready-to-share"
						element={
							!isEmpty(bayToken) && !isNil(bayToken) ? (
								getAccess().isGlobalRole ? (
									<Navigate to="/design-templates" replace />
								) : (
									<Suspense
										fallback={
											<div className="designer-skeleton">
												<BayLoader />
											</div>
										}
									>
										<ReadyToShare />
									</Suspense>
								)
							) : (
								<Navigate to="/login" />
							)
						}
					/>

					<Route
						errorElement={<RouteErrorElement />}
						path="/headline"
						element={<Headline />}
					>
						<Route path=":action" element={<Template />}>
							<Route path=":actionOptions" element={<Template />} />
						</Route>
					</Route>

					<Route
						errorElement={<RouteErrorElement />}
						path="/headlines"
						element={
							<Suspense
								fallback={
									<div className="designer-skeleton">
										<BayLoader />
									</div>
								}
							>
								<Headlines />
							</Suspense>
						}
					/>

					<Route
						errorElement={<RouteErrorElement />}
						path="/template"
						element={
							!isEmpty(bayToken) && !isNil(bayToken) ? (
								<Suspense
									fallback={
										<div className="designer-skeleton">
											<BayLoader />
										</div>
									}
								>
									<Template />
								</Suspense>
							) : (
								<Navigate to="/login" />
							)
						}
					>
						<Route path=":action" element={<Template />}>
							<Route path=":actionOptions" element={<Template />} />
						</Route>
					</Route>

					<Route
						errorElement={<RouteErrorElement />}
						path="/email-block"
						element={
							!isBlockEditorEnabled ? (
								<NotFoundPage />
							) : !isEmpty(bayToken) && !isNil(bayToken) ? (
								<Suspense
									fallback={
										<div className="designer-skeleton">
											<BayLoader />
										</div>
									}
								>
									<Template editorVariant="emailBlock" />
								</Suspense>
							) : (
								<Navigate to="/login" />
							)
						}
					>
						<Route
							path=":action"
							element={
								isBlockEditorEnabled ? (
									<Template editorVariant="emailBlock" />
								) : (
									<NotFoundPage />
								)
							}
						>
							<Route
								path=":actionOptions"
								element={
									isBlockEditorEnabled ? (
										<Template editorVariant="emailBlock" />
									) : (
										<NotFoundPage />
									)
								}
							/>
						</Route>
					</Route>

					<Route
						errorElement={<RouteErrorElement />}
						path="/design-templates"
						element={
							!isEmpty(bayToken) && !isNil(bayToken) ? (
								<Suspense
									fallback={
										<div className="designer-skeleton">
											<BayLoader />
										</div>
									}
								>
									<Templates />
								</Suspense>
							) : (
								<Navigate to="/login" />
							)
						}
					>
						<Route path=":navKey" element={<Templates />}>
							<Route path=":navLabel" element={<Templates />} />
						</Route>
					</Route>

					<Route
						errorElement={<RouteErrorElement />}
						path="/email-templates"
						element={
							!isEmpty(bayToken) && !isNil(bayToken) ? (
								<Suspense
									fallback={
										<div className="designer-skeleton">
											<BayLoader />
										</div>
									}
								>
									<EmailTemplates />
								</Suspense>
							) : (
								<Navigate to="/login" />
							)
						}
					>
						<Route path=":navKey" element={<EmailTemplates />}>
							<Route path=":navLabel" element={<EmailTemplates />} />
						</Route>
					</Route>

					<Route
						errorElement={<RouteErrorElement />}
						path="/email-blocks"
						element={
							!isBlockEditorEnabled ? (
								<NotFoundPage />
							) : !isEmpty(bayToken) && !isNil(bayToken) ? (
								<Suspense
									fallback={
										<div className="designer-skeleton">
											<BayLoader />
										</div>
									}
								>
									<EmailBlockLibrary />
								</Suspense>
							) : (
								<Navigate to="/login" />
							)
						}
					>
						<Route
							path=":navKey"
							element={
								isBlockEditorEnabled ? (
									<EmailBlockLibrary />
								) : (
									<NotFoundPage />
								)
							}
						>
							<Route
								path=":navLabel"
								element={
									isBlockEditorEnabled ? (
										<EmailBlockLibrary />
									) : (
										<NotFoundPage />
									)
								}
							/>
						</Route>
					</Route>

					<Route
						errorElement={<RouteErrorElement />}
						path="/brand-assets"
						element={
							!isEmpty(bayToken) && !isNil(bayToken) ? (
								<Suspense
									fallback={
										<div className="designer-skeleton">
											<BayLoader />
										</div>
									}
								>
									<BrandAssets />
								</Suspense>
							) : (
								<Navigate to="/login" />
							)
						}
					/>

					{/* Settings Routes */}
					<Route errorElement={<RouteErrorElement />} path="/settings">
						<Route
							path="general"
							element={
								!isEmpty(bayToken) && !isNil(bayToken) ? (
									<Suspense
										fallback={
											<div className="designer-skeleton">
												<BayLoader />
											</div>
										}
									>
										<GeneralSettings />
									</Suspense>
								) : (
									<Navigate to="/login" />
								)
							}
						/>
						<Route
							path="categories"
							element={
								!isEmpty(bayToken) && !isNil(bayToken) ? (
									<Suspense
										fallback={
											<div className="designer-skeleton">
												<BayLoader />
											</div>
										}
									>
										<CategoriesSettings />
									</Suspense>
								) : (
									<Navigate to="/login" />
								)
							}
						/>
						<Route
							path="tags"
							element={
								!isEmpty(bayToken) && !isNil(bayToken) ? (
									<Suspense
										fallback={
											<div className="designer-skeleton">
												<BayLoader />
											</div>
										}
									>
										<TagsSettings />
									</Suspense>
								) : (
									<Navigate to="/login" />
								)
							}
						/>
						<Route
							path="keywords"
							element={
								!isEmpty(bayToken) && !isNil(bayToken) ? (
									<Suspense
										fallback={
											<div className="designer-skeleton">
												<BayLoader />
											</div>
										}
									>
										<KeywordsSettings />
									</Suspense>
								) : (
									<Navigate to="/login" />
								)
							}
						/>
						<Route
							path="presets"
							element={
								!isEmpty(bayToken) && !isNil(bayToken) ? (
									<Suspense
										fallback={
											<div className="designer-skeleton">
												<BayLoader />
											</div>
										}
									>
										<PresetsSettings />
									</Suspense>
								) : (
									<Navigate to="/login" />
								)
							}
						/>
						{/* Default redirect to categories (visible to all roles) */}
						<Route index element={<Navigate to="categories" replace />} />
					</Route>

					{/* Backwards compatibility for moved routes - keep path and query */}
					<Route
						path="/templates/*"
						element={<LegacyRedirect from="/templates" to="/design-templates" />}
					/>
					<Route
						path="/email-block-library/*"
						element={
							<LegacyRedirect from="/email-block-library" to="/email-blocks" />
						}
					/>
					<Route
						path="/template-settings/*"
						element={<LegacyRedirect from="/template-settings" to="/settings" />}
					/>

					{/* Backwards compatibility for design-studio route */}
					<Route
						path="/design-studio"
						element={<Navigate to="/design-templates" replace />}
					/>

					<Route
						errorElement={<RouteErrorElement />}
						path="/login"
						element={
							isEmpty(bayToken) && isNil(bayToken) ? (
								<Suspense
									fallback={
										<div className="designer-skeleton">
											<BayLoader />
										</div>
									}
								>
									<Login />
								</Suspense>
							) : (
								<Navigate to="/" replace />
							)
						}
					/>

					<Route
						errorElement={<RouteErrorElement />}
						path="/logout"
						element={
							<Suspense
								fallback={
									<div className="designer-skeleton">
										<BayLoader />
									</div>
								}
							>
								<Logout />
							</Suspense>
						}
					/>

					{/* Overlay engine test surface. No auth guard -- see the note on the
					    lazy import above. Remove when phase 2 lands. */}
					<Route
						errorElement={<RouteErrorElement />}
						path="/float-lab"
						element={
							<Suspense fallback={<BayLoader />}>
								<FloatManagerLab />
							</Suspense>
						}
					/>

					{/* Tooltip engine comparison surface. Same terms as /float-lab:
					    no auth guard, remove when the BayTooltip migration lands. */}
					<Route
						errorElement={<RouteErrorElement />}
						path="/tooltip-lab"
						element={
							<Suspense fallback={<BayLoader />}>
								<TooltipLab />
							</Suspense>
						}
					/>

					<Route
						errorElement={<RouteErrorElement />}
						path="*"
						element={<NotFoundPage />}
					/>
				</Route>,
			),
		);

	const ToastWrapper = () => {
		const dispatch = useDispatch();
		const toast = useSelector((state) => state.toast);
		return <BayToast onClose={() => dispatch(closeToast())} {...toast} />;
	};

	return (
		<ErrorBoundary
			FallbackComponent={FallbackErrorElement}
			onError={logError}
			onReset={(details) => {
				// Reset the state of your app so the error doesn't happen again
				window.location.reload();
			}}
		>
			<RouterProvider
				router={router}
				fallbackElement={
					<div className="designer-skeleton">
						<BayLoader />
					</div>
				}
			/>

			<ToastWrapper />

			{/* global notifications dialog */}
			{!isNil(notificationState) && (
				<BayModal
					title={notificationState.title}
					size={notificationState.size}
					backdrop={notificationState.backdrop}
					show={notificationState.show}
					closeButton={false}
					variant={'danger'}
					buttonText={'Ok'}
					hideCancel={true}
					submitFunc={() =>
						notificationState.redirectOnOk
							? globalDispatch(globalState.resetNotificationState())
							: null
					}
				>
					<span>{notificationState.body}</span>
				</BayModal>
			)}
		</ErrorBoundary>
	);
};

export default Router;
